import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Segment,
} from 'semantic-ui-react';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';

import { fetchM } from '../actions/fetchM';
import { flashSuccessMessage, flashErrorMessage } from '../actions/flash';
import BankerItem from './BankerItem';
import BankersList from '../containers/modals/BankersList';
import InviteBankerForm from '../containers/modals/InviteBankerForm';

const DragHandle = SortableHandle(({ idx, disabled }) => (
  <Grid columns="equal" centered verticalAlign="middle">
    <Grid.Column style={{ color: '#bbbbbb' }}>
      {idx + 1}
    </Grid.Column>
    <Grid.Column>
      <Icon size="large" disabled={disabled} name="content" />
    </Grid.Column>
  </Grid>
));

const SortableItem = SortableElement(({ data, idx, onDeleteItem, itemDisabled }) => (
  <BankerItem
    custom
    showSummary
    banker={data}
    disabled={itemDisabled}
    leftComponent={<DragHandle idx={idx} disabled={itemDisabled} />}
    rightComponent={
      <Button
        onClick={e => onDeleteItem(e, idx)}
        negative
        icon="trash"
        compact
        disabled={itemDisabled}
      />
    }
  />
));

const SortableList = SortableContainer(({ items, onDeleteItem, disabled }) => (
  <Segment className={items.length > 0 ? 'sortable' : ''} textAlign={items.length > 0 ? null : 'center'}>
    {
      items.length > 0 ? items.map((data, index) => (
        <SortableItem
          data={data}
          idx={index} // SortableElement didn't pass index props, so we need to add idx here
          index={index}
          key={`item-${index}`}
          onDeleteItem={onDeleteItem}
          disabled={disabled}
          itemDisabled={disabled}
        />
      )) : (
        <p>No bankers.</p>
      )
    }
  </Segment>
));

export default class ProjectPanelBankers extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    allowAgentSalesTeamBankerInvitation: PropTypes.bool,
    autoAssignStatus: PropTypes.string,
    autoShareFormToBanker: PropTypes.bool,
    bankerSelectionTabs: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    defaultBookingStatusID: PropTypes.string,
    projectID: PropTypes.string.isRequired,
    token: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func,
    onCheckboxChange: PropTypes.func,
    onStatusChange: PropTypes.func,
    disabled: PropTypes.bool,
    auth: PropTypes.shape({
      company: PropTypes.object,
    }),
  }
  static defaultProps = {
    data: [],
    token: '',
    onChange: null,
    disabled: false,
  }
  static getPropsToState = (props) => {
    const data = props.data || [];
    const bankerSelectionTabs = props.bankerSelectionTabs || [];
    return {
      bankerSelectionTabs,
      data,
    };
  }
  constructor(props) {
    super(props);
    const state = ProjectPanelBankers.getPropsToState(props);
    this.state = {
      ...state,
      showAddBanker: false,
      showInviteBanker: false,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = ProjectPanelBankers.getPropsToState(nextProps);
    if (JSON.stringify(this.props.data) !== JSON.stringify(nextProps.data)) {
      this.setState({ ...state });
    }
  }

  onSortEnd = ({ oldIndex, newIndex }) =>
    this.sortDataByIndex(arrayMove(this.state.data, oldIndex, newIndex));
  setLoading = (loading = false) => (new Promise((res) => {
    this.setState({ loading }, res);
  }))
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  getAutoAssignOptions = () => [
    { text: 'Booked', value: 'booked' },
    { text: 'Reserved', value: 'reserved' },
  ];
  getSortedData = data => data.sort((a, b) => a.position - b.position);
  sortDataByIndex = (newData) => {
    const data = newData;
    data.forEach((d, index) => {
      data[index].position = index + 1;
    });
    this.setState({ data }, () => {
      const { onChange } = this.props;
      if (onChange) {
        onChange(data);
      }
    });
  }
  handleShowAddBanker = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showAddBanker: true,
    });
  }
  handleCloseAddBanker = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showAddBanker: false,
    });
  }
  handleAddBanker = (e, data) => {
    if (e) { e.preventDefault(); }
    this.setState({
      data,
      showAddBanker: false,
    }, () => {
      const { onChange } = this.props;
      if (onChange) {
        onChange(data);
      }
    });
  }
  handleShowInviteBanker = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showInviteBanker: true,
    });
  }
  handleCloseInviteBanker = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showInviteBanker: false,
    });
  }
  async inviteBanker(validations, newBanker) {
    const { store } = this.context;
    const errors = validations.filter(v => !v.isValid);
    if (errors.length > 0) {
      store.dispatch(flashErrorMessage({ header: 'Please fix form errors', list: errors.map(l => l.message) }));
      return;
    }
    await this.setLoading(true);
    try {
      await store.dispatch(fetchM(`/projects/${this.props.projectID}/bankers/invite/`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
        body: JSON.stringify(newBanker),
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: 'There was an error with your request. Please try again.' }));
      return;
    }
    await this.setLoading(false);
    await this.setComponentState({ showAddBanker: false, showInviteBanker: false });
    store.dispatch(flashSuccessMessage({ header: 'Invite Success', message: `Banker ${newBanker.name} is successfully invited.` }));
  }
  handleInviteBanker = (e, validation, banker) => {
    if (e) { e.preventDefault(); }
    this.inviteBanker(validation, banker);
  }
  handleOnChangeAutoAssignStatus = (e, value) => {
    const { onStatusChange } = this.props;
    if (onStatusChange) {
      onStatusChange(value.value);
    }
  }
  handleCheckBoxChange = (e, value) => {
    const { onCheckboxChange } = this.props;
    if (onCheckboxChange) {
      onCheckboxChange(value.name);
    }
  }
  handleOnDelete = (e, index) => {
    if (e) { e.preventDefault(); }
    const data = this.state.data;
    data.splice(index, 1);
    this.sortDataByIndex(data);
  }
  render() {
    const { allowAgentSalesTeamBankerInvitation, autoAssignStatus, autoShareFormToBanker, bankerSelectionTabs, defaultBookingStatusID, disabled } = this.props;
    const { data } = this.state;
    return (
      <Grid className="ProjectPanelBankers">
        <InviteBankerForm
          auth={this.props.auth}
          ref={(ref) => { this.form = ref; }}
          open={this.state.showInviteBanker}
          onClose={this.handleCloseInviteBanker}
          onConfirm={this.handleInviteBanker}
        />
        <Divider horizontal>Bankers Selection Settings</Divider>
        <Header as="h4">Auto-assign</Header>
        <Label content="Auto-assign case to Bankers when status is" />
        <Dropdown
          onChange={this.handleOnChangeAutoAssignStatus}
          options={this.getAutoAssignOptions()}
          selection
          value={autoAssignStatus}
          disabled={defaultBookingStatusID === 'booked'}
        />
        <Header as="h4">
          {'Invite new bankers during unit booking '}
          <small style={{ fontWeight: 100 }}>(Applies to all user except Headquarters role)</small>
        </Header>
        <Segment className="form-sof-checkbox">
          <Form.Checkbox
            toggle
            label={allowAgentSalesTeamBankerInvitation ? 'On' : 'Off'}
            name="allowAgentSalesTeamBankerInvitation"
            checked={allowAgentSalesTeamBankerInvitation}
            onChange={this.handleCheckBoxChange}
          />
        </Segment>
        <Header as="h4">Auto-share</Header>
        <Label content="Automatically share booking form once Banker is assigned" />
        <Segment className="form-sof-checkbox">
          <Form.Checkbox
            toggle
            label={autoShareFormToBanker ? 'On' : 'Off'}
            name="autoShareFormToBanker"
            checked={autoShareFormToBanker}
            onChange={this.handleCheckBoxChange}
          />
        </Segment>
        <Divider horizontal>Bankers Selection Tabs</Divider>
        <Grid.Row>
          <Grid.Column computer={7} tablet={16} mobile={16}>
            <Form.Group grouped>
              <Form.Group>
                <Form.Field>
                  <Segment className="form-sof-segment">
                    <Header as="h4">Visibility settings</Header>
                    <Form.Group className="form-group">
                      <Form.Field className="form-label" label="Panel (ON by default)" />
                      <Form.Field className="form-value">
                        <Form.Checkbox
                          toggle
                          label="On"
                          name="panel_banker"
                          checked
                          disabled
                        />
                      </Form.Field>
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Field className="form-label" label="Preferred" />
                      <Form.Field className="form-value">
                        <Form.Checkbox
                          toggle
                          label={bankerSelectionTabs && bankerSelectionTabs.length > 0 && bankerSelectionTabs.some(o => o === 'preferred_banker') ? 'On' : 'Off'}
                          name="preferred_banker"
                          checked={bankerSelectionTabs && bankerSelectionTabs.length > 0 && bankerSelectionTabs.includes('preferred_banker')}
                          onChange={this.handleCheckBoxChange}
                        />
                      </Form.Field>
                    </Form.Group>
                    <Form.Group className="form-group">
                      <Form.Field className="form-label" label="All" />
                      <Form.Field className="form-value">
                        <Form.Checkbox
                          toggle
                          label={bankerSelectionTabs && bankerSelectionTabs.length > 0 && bankerSelectionTabs.some(o => o === 'all') ? 'On' : 'Off'}
                          name="all"
                          checked={bankerSelectionTabs && bankerSelectionTabs.length > 0 && bankerSelectionTabs.includes('all')}
                          onChange={this.handleCheckBoxChange}
                        />
                      </Form.Field>
                    </Form.Group>
                  </Segment>
                </Form.Field>
              </Form.Group>
            </Form.Group>
          </Grid.Column>
        </Grid.Row>
        <Divider horizontal>Bankers List</Divider>
        <BankersList
          open={this.state.showAddBanker}
          onClose={this.handleCloseAddBanker}
          onConfirm={this.handleAddBanker}
          selected={data}
          only
          extraNode={(
            <Button
              floated="left"
              onClick={this.handleShowInviteBanker}
              content="Invite"
            />
          )}
        />
        <Grid.Row>
          <Grid.Column width={16}>
            <SortableList
              helperClass="projectPanelBankers isSorting"
              items={this.getSortedData(data)}
              lockAxis="y"
              lockToContainerEdges
              onDeleteItem={this.handleOnDelete}
              onSortEnd={this.onSortEnd}
              useDragHandle
              disabled={disabled}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16} textAlign="center">
            <Button
              primary
              content="Add Banker"
              onClick={this.handleShowAddBanker}
              disabled={disabled}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ProjectPanelBankers.jsx